import { AppDispatch } from '@/store/store';
import { Services } from '@/infrastructure/services';
import { AssetInstance, isAnchorCategory } from '@/types/assetTypes';
import { addSceneAsset, removeSceneAssets } from '@/features/sceneAssets/sceneAssetsActions';
import { clearKeyframesForAsset, selectKeyframesByAssetId, upsertKeyframe } from '@/features/keyframes/keyframesSlice';
import { clearClipsForAsset, selectClipsByAssetId, upsertClip } from '@/features/clips/clipsSlice';
import { globalLibraryCache } from '@/modules/library/libraryCache';
import { RootState } from '@/store/rootState';
import { reconcileStoryAttributionsForScene } from '@/modules/library/utils/storyAttributionUtils';
import { addUIElement, removeUIElement, selectUIElementById } from '@/features/uiElements/uiElementsSlice';
import { toast } from 'sonner';

import { isGesture, type Gesture, type WriteScope } from '@/modules/undoRedo/UndoStack';

type RemoveOptions = {
  clearCache?: boolean;
  /** Resolves once the row is deleted and rejects when it is not, so an anchor's replacement never finds the old one or is added beside it. */
  awaitDbCleanup?: boolean;
  /** The gesture the removal is part of (a swap), or untracked when it is no edit to undo (its library item is being deleted); else its own undo step. */
  scope?: WriteScope;
};

/**
 * Consolidated asset removal function
 * Handles complete cleanup: keyframes, clips, asset instance, Redux state, renderer notification
 * 
 * @param assetInstanceId - The asset instance ID to remove
 * @param sceneInstanceId - The scene instance ID
 * @param dispatch - Redux dispatch function
 * @param services - Services instance
 * @param getState - Redux getState function to access current state
 * @param options - Optional configuration
 */
export async function removeAssetFromScene(
  assetInstanceId: string,
  sceneInstanceId: string,
  dispatch: AppDispatch,
  services: Services,
  getState: () => RootState,
  options?: RemoveOptions
): Promise<void> {
  console.log('🗑️ Removing asset and all related data:', assetInstanceId);

  // Read as it is called, so removals in a row (a library item's placements) each take only their own
  const listed = getState().sceneAssets.sceneAssetsArray as AssetInstance[];
  const at = listed.findIndex((a: AssetInstance) => a.instanceId === assetInstanceId);
  const assetInstance = at < 0 ? undefined : listed[at];

  // A delete that fails leaves the row saved and in the renderer (which hears of a removal only after it
  // succeeds): the editor shows it again where it was, so all three agree, and the user is told
  const putBack = () => {
    if (assetInstance) dispatch(addSceneAsset(assetInstance, at));
  };
  const deleteFailed = (error: unknown) => {
    const reason = error instanceof Error ? error.message : String(error);
    toast.error(`${assetInstance?.instanceDisplayName || 'The asset'} could not be deleted and is still in the scene: ${reason}`);
  };

  // Screen UI elements: remove via UI element service and Redux, then return
  if (assetInstance?.instanceCategory === 'screen_ui') {
    const element = selectUIElementById(getState(), assetInstanceId);
    dispatch(removeSceneAssets([assetInstanceId]));
    dispatch(removeUIElement({ instanceId: assetInstanceId, sceneInstanceId }));
    try {
      await services.uiElement.deleteInstance(assetInstanceId, options?.scope);
      console.log('✅ Screen UI element removed (DB + renderer notified)');
    } catch (error) {
      console.error('❌ Failed to delete UI element from database:', error);
      putBack();
      if (element) dispatch(addUIElement(element));
      deleteFailed(error);
      if (options?.awaitDbCleanup) throw error;
    }
    return;
  }

  // What the editor shows of it, to show again if the delete fails
  const keyframes = selectKeyframesByAssetId(getState(), assetInstanceId);
  const clips = selectClipsByAssetId(getState(), assetInstanceId);

  // STEP 1: Remove from UI immediately (timeline/Redux) - instant feedback
  dispatch(removeSceneAssets([assetInstanceId]));
  dispatch(clearKeyframesForAsset(assetInstanceId));
  dispatch(clearClipsForAsset(assetInstanceId));
  console.log('✅ Updated Redux state (removed asset, keyframes, and clips)');

  services.analytics.trackLureoAssetRemoved({
    scene_instance_id: sceneInstanceId,
    asset_category: assetInstance?.instanceCategory,
    asset_name: assetInstance?.instanceDisplayName,
  });

  // STEP 2: Patch library cache to remove asset from 'scene' sections (no full reload)
  if (options?.clearCache && assetInstance?.typeId) {
    const cacheKey = `${sceneInstanceId}_defaultAssets`;
    const cached = globalLibraryCache[cacheKey];
    if (cached) {
      const newData = { ...cached.data };
      let changed = false;
      Object.keys(newData).forEach(sectionKey => {
        const section = newData[sectionKey];
        if (section.type === 'scene') {
          const filtered = section.assets.filter(a => a.typeId !== assetInstance.typeId);
          if (filtered.length !== section.assets.length) {
            newData[sectionKey] = { ...section, assets: filtered };
            changed = true;
          }
        }
      });
      if (changed) {
        globalLibraryCache[cacheKey] = { ...cached, data: newData };
        window.dispatchEvent(new CustomEvent('libraryCachePatched'));
        console.log('✅ Patched library cache (removed from scene section)');
      }
    }
  }

  // STEP 3: Database cleanup in background (non-blocking); the deleted asset is then removed from the renderer.
  // UI is already updated, and put back if the delete fails. One delete takes its keyframes and clips along, so
  // its undo puts all of them back

  // The delete and the anchor re-sync are one undo step; the story's location stays for the next anchor's sync to keep or move
  const deleteRow = async (gesture: WriteScope) => {
    try {
      await services.asset.deleteAssetInstanceRow(assetInstanceId, undefined, gesture);
    } catch (error) {
      console.error('❌ Failed to delete asset from database:', error);
      putBack();
      for (const keyframe of keyframes) dispatch(upsertKeyframe(keyframe));
      for (const clip of clips) dispatch(upsertClip(clip));
      deleteFailed(error);
      throw error;
    }
    console.log('✅ Deleted asset from database');
    try {
      await reconcileStoryAttributionsForScene(sceneInstanceId);
    } catch (err) {
      console.warn('⚠️ Failed to reconcile story attributions:', err);
    }
    // Anchor swap: the row is now gone, so re-sync SceneProvider (cached scene row +
    // derived anchor category) — otherwise the swapped anchor's state only appears
    // after a full page reload (lureo-docs#259).
    if (isAnchorCategory(assetInstance?.instanceCategory)) {
      window.dispatchEvent(new CustomEvent('sceneAnchorChanged'));
    }
    console.log('✅ Database cleanup complete');
  };
  const scope = options?.scope;
  const dbCleanup = scope && !isGesture(scope)
    ? deleteRow(scope)
    : services.undo.transaction(`Delete ${assetInstance?.instanceDisplayName || 'asset'}`, deleteRow, scope as Gesture | undefined);

  // Sequence the delete when asked (anchor swap) so a follow-up add's one-anchor gate
  // reads the post-delete DB rather than racing the in-flight deletion (lureo-docs#259).
  if (options?.awaitDbCleanup) {
    await dbCleanup;
  } else {
    // Already shown to the user
    dbCleanup.catch(() => undefined);
  }

  console.log('✅ Asset removal complete (UI updated, database cleanup in progress)');
}

/** removeAssetFromScene for a caller that still passes the asset list, which is not read. */
export const handleRemoveAsset = (
  assetInstanceId: string,
  sceneInstanceId: string,
  _sceneAssetsArray: AssetInstance[],
  dispatch: AppDispatch,
  services: Services,
  getState: () => RootState,
  options?: RemoveOptions
): Promise<void> => removeAssetFromScene(assetInstanceId, sceneInstanceId, dispatch, services, getState, options);
